import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { IRichContentEditorState } from '../../../extViewmodels';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { FileSendLinkPlaceholder } from '../../../models/Token';
import { contentHasFileLinkToken } from '../../../models/utils/richContentTokenUtils';
import { AttachInputButton } from '../../components/AttachInputButton';
import { Checkbox } from '../../components-v2/Checkbox';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { EmojiPickerButton } from '../../components/texting/EmojiPickerButton';
import { MergeFieldsDropdown } from '../../components/texting/MergeFieldsDropdown';
import { OutboundTextMessageBubble } from '../../components/texting/MessageBubble/OutboundTextMessageBubble';
import { brandPrimary, navigation, oldSilver, urgentDate } from '../../styles/colors';
import { FileImagePreview } from './FileImagePreview';
import { StoredAttachmentPreview } from './StoredAttachmentPreview';
import { styleSheet } from './styles';
import { TextCampaignComposer, type ITextCampaignComposerHandle } from './TextCampaignComposer';
import { buildCampaignComposerTokens, FILE_SEND_SYMBOLS } from './textCampaignComposerContent';

const TEXT_CAMPAIGN_MAX_CHARACTERS = 1600;
const SEND_AS_LINK_TOGGLE_ID = 'textCampaign-send-as-link';

export function TextCampaignPageBody({
	contentEditorState,
	attachmentFiles,
	storedAttachments,
	onAttachFiles,
	onRemoveAttachmentFile,
	onContentStateChanged,
	onRemoveStoredAttachment,
	sendAttachmentsAsLink = false,
	onSendAttachmentsAsLinkChange,
	canSendFileLinks,
	canRequestFiles,
}: {
	attachmentFiles: File[];
	storedAttachments: Api.IFileAttachment[];
	onAttachFiles: (files: File[]) => void;
	onRemoveAttachmentFile: (file: File) => void;
	onRemoveStoredAttachment: (attachmentId: string) => void;
	contentEditorState: IRichContentEditorState;
	onContentStateChanged: (content: IRichContentEditorState) => void;
	sendAttachmentsAsLink?: boolean;
	onSendAttachmentsAsLinkChange?: (value: boolean) => void;
	canSendFileLinks?: boolean;
	canRequestFiles?: boolean;
}) {
	const composerRef = React.useRef<ITextCampaignComposerHandle>(null);
	const [displayedCharacterCount, setDisplayedCharacterCount] = React.useState(0);
	const errorMessages = useErrorMessages();
	const hasAttachments = attachmentFiles.length > 0 || storedAttachments.length > 0;

	const tokens = React.useMemo(
		() => buildCampaignComposerTokens({ canSendFileLinks, canRequestFiles }),
		[canSendFileLinks, canRequestFiles]
	);

	const totalAttachmentBytes =
		attachmentFiles.reduce((sum, f) => sum + f.size, 0) + storedAttachments.reduce((sum, a) => sum + a.fileSize, 0);
	const isTotalSizeLarge = totalAttachmentBytes > Api.TextMessagingMaxFileByteSize;
	const hasFileLinkToken = contentHasFileLinkToken(contentEditorState?.getRawRichTextContent());

	React.useEffect(() => {
		const textContent = contentEditorState?.getPlainTextPreview(false);
		if (textContent) {
			setDisplayedCharacterCount(textContent.length);
		}
	}, [contentEditorState]);

	// Large attachments can only go out as a secure link, so force it on and drop in the merge field.
	React.useEffect(() => {
		if (isTotalSizeLarge && !sendAttachmentsAsLink && canSendFileLinks) {
			onSendAttachmentsAsLinkChange?.(true);
			if (!composerRef.current?.hasFileSendToken()) {
				composerRef.current?.insertTokenBySymbol(FileSendLinkPlaceholder.symbol);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTotalSizeLarge]);

	// Nothing to link once the attachments are gone: reset the toggle and strip the merge field.
	React.useEffect(() => {
		if (!hasAttachments && sendAttachmentsAsLink) {
			onSendAttachmentsAsLinkChange?.(false);
			composerRef.current?.removeFileSendTokens();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasAttachments]);

	const handleInsertSymbol = (symbol: string) => {
		composerRef.current?.insertTokenBySymbol(symbol);
		if (hasAttachments && !sendAttachmentsAsLink && FILE_SEND_SYMBOLS.includes(symbol)) {
			onSendAttachmentsAsLinkChange?.(true);
		}
	};

	const handleToggleSendAsLink = (checked: boolean) => {
		onSendAttachmentsAsLinkChange?.(checked);
		if (checked) {
			if (!composerRef.current?.hasFileSendToken()) {
				composerRef.current?.insertTokenBySymbol(FileSendLinkPlaceholder.symbol);
			}
		} else {
			composerRef.current?.removeFileSendTokens();
		}
	};

	const isOverLimit = displayedCharacterCount > TEXT_CAMPAIGN_MAX_CHARACTERS;
	return (
		<>
			<div className={css(styleSheet.editorContainer, hasAttachments && styleSheet.editorContainerWithAttachments)}>
				<div className={css(styleSheet.toolbar)}>
					<div className={css(styleSheet.toolbarLeft)}>
						<EmojiPickerButton
							onEmojiClick={(_, data) => {
								composerRef.current?.insertText(data.emoji);
							}}
						/>
						<div className={css(styleSheet.toolbarDivider)} />
						<AttachInputButton
							multiple={true}
							onChange={ev => {
								if (!ev.target.files) {
									return;
								}
								const files = Array.from(ev.target.files).filter(file =>
									attachmentFiles.every(f => f.name !== file.name)
								);
								const nextTotal =
									[...attachmentFiles, ...files].reduce((sum, f) => sum + f.size, 0) +
									storedAttachments.reduce((sum, a) => sum + a.fileSize, 0);
								ev.target.value = null;
								if (nextTotal > Api.TextMessagingMaxLinkFileByteSize) {
									errorMessages.push({
										messages: [
											`Total attached file size must not exceed ${Api.convertToMB(
												Api.TextMessagingMaxLinkFileByteSize / Api.ONE_KB
											)}.`,
										],
									});
									return;
								}
								onAttachFiles(files);
							}}
						/>
						<div className={css(styleSheet.toolbarDivider)} />
						<MergeFieldsDropdown
							onInsertSymbol={handleInsertSymbol}
							canSendFileLinks={canSendFileLinks}
							canRequestFiles={canRequestFiles}
						/>
						<div className={css(styleSheet.toolbarDivider)} />
					</div>
					<span
						className={css(styleSheet.characterCount)}
						style={{
							color: isOverLimit ? urgentDate : navigation,
							fontWeight: isOverLimit ? 'bold' : 'normal',
						}}
					>
						{displayedCharacterCount.toLocaleString()} / {TEXT_CAMPAIGN_MAX_CHARACTERS.toLocaleString()}
					</span>
				</div>
				<div className={css(styleSheet.editorWrapper)}>
					<TextCampaignComposer
						ref={composerRef}
						contentState={contentEditorState}
						tokens={tokens}
						onContentStateChanged={onContentStateChanged}
						placeholder='Write your message…'
					/>
				</div>
			</div>
			{hasAttachments ? (
				<div className={css(styleSheet.attachmentsContainer)}>
					{storedAttachments.map(attachment => {
						return (
							<StoredAttachmentPreview
								attachment={attachment}
								key={attachment.url}
								onClose={() => onRemoveStoredAttachment(attachment.id)}
							/>
						);
					})}
					{attachmentFiles.map((file, i) => {
						return file.type.includes('image/') ? (
							<div className={css(styleSheet.imageAttachmentWrapper)} key={i}>
								<FileImagePreview
									className={css(styleSheet.attachmentImagePreview)}
									file={file}
									loadingEl={<LoadingSpinner type='extra-small' />}
								/>
								<DeprecatedCloseButton
									className={css(styleSheet.attachmentCloseButton)}
									fillColor={oldSilver}
									onClick={() => {
										onRemoveAttachmentFile(file);
									}}
									opacity={0.9}
									outlineColor='#FFF'
									outlineWidth={1.5}
									widthAndHeight={16}
									xMark={true}
								/>
							</div>
						) : (
							<div className={css(styleSheet.fileAttachmentContainer)} key={i}>
								<span className={css(styleSheet.fileAttachmentName)}>{file.name}</span>
								<DeprecatedCloseButton
									fillColor={oldSilver}
									onClick={() => onRemoveAttachmentFile(file)}
									opacity={0.9}
									outlineColor='#FFF'
									outlineWidth={1.5}
									styles={[styleSheet.fileAttachmentCloseButton]}
									widthAndHeight={16}
									xMark={false}
								/>
							</div>
						);
					})}
				</div>
			) : null}
			{canSendFileLinks && hasAttachments ? (
				<div className={css(styleSheet.sendAsLinkRow)}>
					<Checkbox
						id={SEND_AS_LINK_TOGGLE_ID}
						checked={isTotalSizeLarge || sendAttachmentsAsLink}
						disabled={isTotalSizeLarge}
						onCheckedChange={value => handleToggleSendAsLink(value === true)}
					/>
					<label className={css(styleSheet.sendAsLinkLabel)} htmlFor={SEND_AS_LINK_TOGGLE_ID}>
						{isTotalSizeLarge ? 'Send as download link (required for large files)' : 'Send as download link'}
					</label>
				</div>
			) : null}
			{sendAttachmentsAsLink && !hasFileLinkToken ? (
				<p className={css(styleSheet.sendAsLinkWarning)}>{'Add a {{send files}} merge field to your message.'}</p>
			) : null}
			<OutboundTextMessageBubble backgroundColor={brandPrimary}>
				If you&apos;d prefer not to hear from me by text message, please reply back with &quot;Stop&quot;.
			</OutboundTextMessageBubble>
			<div className={css(styleSheet.canSpamDisclaimer)}>*Required by CAN-SPAM</div>
		</>
	);
}
